// Pure helpers shared by the listing forms (Create Listing, Create Related
// Listing). They build and validate the body for
// POST product/{id}/listing in its create-a-listing mode (body has
// `platform`), and normalize GET product/{id}/listings for display.
// No React or axios imports here, so this file is unit-testable as is.

export type ListingPlatform = "amazon" | "walmart" | "shopify";

export type ListingOrigin = "create_listing" | "create_related_listing";

export const LISTING_PLATFORMS: ReadonlyArray<{
  value: ListingPlatform;
  label: string;
}> = [
  { value: "amazon", label: "Amazon" },
  { value: "walmart", label: "Walmart" },
  { value: "shopify", label: "Shopify" },
];

export function isListingPlatform(value: unknown): value is ListingPlatform {
  return LISTING_PLATFORMS.some((p) => p.value === value);
}

export function platformLabel(platform: string | null | undefined): string {
  return (
    LISTING_PLATFORMS.find((p) => p.value === platform)?.label ??
    (platform || "—")
  );
}

/** Form state for every platform's identifiers. Only the selected platform's
 *  values are validated and sent, so switching platform keeps what was typed. */
export interface PlatformIdentifiers {
  asin: string;
  msku: string;
  walmartSku: string;
  walmartItemId: string;
  shopifySku: string;
  shopifyProductId: string;
}

export const EMPTY_IDENTIFIERS: PlatformIdentifiers = {
  asin: "",
  msku: "",
  walmartSku: "",
  walmartItemId: "",
  shopifySku: "",
  shopifyProductId: "",
};

/** Input limits that mirror the DB columns the values land in. */
export const IDENTIFIER_MAX_LENGTHS: Record<keyof PlatformIdentifiers, number> =
  {
    asin: 10, // amazon_listings.asin (ASINs are always 10)
    msku: 30, // amazon_listings.msku varchar(30)
    walmartSku: 50, // walmart_listings.sku varchar(50)
    walmartItemId: 20, // walmart_listings.item_id varchar(20)
    shopifySku: 255, // shopify_listings.sku varchar(255)
    shopifyProductId: 20, // shopify_listings.shopify_product_id bigint unsigned
  };

export const LISTING_TITLE_MAX_LENGTH = 500; // listing_title varchar(500)

export const ASIN_PATTERN = /^[A-Z0-9]{10}$/;

// shopify_listings.shopify_product_id is BIGINT UNSIGNED.
const BIGINT_UNSIGNED_MAX = "18446744073709551615";

export function normalizeAsin(value: string): string {
  return value.trim().toUpperCase();
}

export type IdentifierErrors = Partial<
  Record<keyof PlatformIdentifiers, string>
>;

/** Identifiers are optional; this only checks the format of what was entered
 *  for the selected platform. */
export function validatePlatformIdentifiers(
  platform: ListingPlatform,
  identifiers: PlatformIdentifiers
): IdentifierErrors {
  const errors: IdentifierErrors = {};
  if (platform === "amazon") {
    const asin = normalizeAsin(identifiers.asin);
    if (asin && !ASIN_PATTERN.test(asin)) {
      errors.asin = "ASIN must be exactly 10 alphanumeric characters";
    }
  }
  if (platform === "shopify") {
    const productId = identifiers.shopifyProductId.trim();
    if (productId) {
      if (!/^\d+$/.test(productId)) {
        errors.shopifyProductId = "Product ID must contain only digits";
      } else if (
        productId.length > BIGINT_UNSIGNED_MAX.length ||
        (productId.length === BIGINT_UNSIGNED_MAX.length &&
          productId > BIGINT_UNSIGNED_MAX)
      ) {
        errors.shopifyProductId = "Product ID is too large";
      }
    }
  }
  return errors;
}

export function hasIdentifierErrors(errors: IdentifierErrors): boolean {
  return Object.values(errors).some(Boolean);
}

/** True when an Amazon listing has neither ASIN nor MSKU, so the sync can't
 *  match it to the live Amazon listing. */
export function isUnmatchableAmazonListing(
  platform: ListingPlatform,
  identifiers: PlatformIdentifiers
): boolean {
  return (
    platform === "amazon" &&
    !identifiers.asin.trim() &&
    !identifiers.msku.trim()
  );
}

export function validateListingTitle(title: string): string | null {
  const trimmed = title.trim();
  if (!trimmed) return "Listing Title is required";
  if (trimmed.length > LISTING_TITLE_MAX_LENGTH) {
    return `Listing Title must be at most ${LISTING_TITLE_MAX_LENGTH} characters`;
  }
  return null;
}

export interface CreateListingPayload {
  platform: ListingPlatform;
  listing_title: string;
  listing_origin: ListingOrigin;
  created_by?: string;
  asin?: string;
  msku?: string;
  walmart_sku?: string;
  walmart_item_id?: string;
  shopify_sku?: string;
  shopify_product_id?: string;
}

export interface BuildListingPayloadArgs {
  platform: ListingPlatform;
  listingTitle: string;
  listingOrigin: ListingOrigin;
  createdBy?: string;
  identifiers: PlatformIdentifiers;
}

/** Body for POST product/{id}/listing (create mode). Sends only the selected
 *  platform's identifiers and leaves out empty ones. Call it after
 *  validatePlatformIdentifiers passes. */
export function buildListingPayload({
  platform,
  listingTitle,
  listingOrigin,
  createdBy,
  identifiers,
}: BuildListingPayloadArgs): CreateListingPayload {
  const payload: CreateListingPayload = {
    platform,
    listing_title: listingTitle.trim(),
    listing_origin: listingOrigin,
  };
  const createdByTrimmed = createdBy?.trim();
  if (createdByTrimmed) payload.created_by = createdByTrimmed;

  if (platform === "amazon") {
    const asin = normalizeAsin(identifiers.asin);
    const msku = identifiers.msku.trim();
    if (asin) payload.asin = asin;
    if (msku) payload.msku = msku;
  } else if (platform === "walmart") {
    const sku = identifiers.walmartSku.trim();
    const itemId = identifiers.walmartItemId.trim();
    if (sku) payload.walmart_sku = sku;
    if (itemId) payload.walmart_item_id = itemId;
  } else if (platform === "shopify") {
    const sku = identifiers.shopifySku.trim();
    const productId = identifiers.shopifyProductId.trim();
    if (sku) payload.shopify_sku = sku;
    // Sent as a digit string: the bigint can exceed a JS number's precision.
    if (productId) payload.shopify_product_id = productId;
  }
  return payload;
}

// ─── GET product/{id}/listings ──────────────────────────────────────────────

/** One row of the "existing listings" table, across platforms. */
export interface ProductListingSummary {
  key: string;
  platform: string;
  asin: string | null;
  msku: string | null;
  fulfillmentChannel: string | null;
  walmartSku: string | null;
  walmartItemId: string | null;
  shopifySku: string | null;
  shopifyProductId: string | null;
  listingTitle: string | null;
  createdBy: string | null;
  isCompetitorListing: boolean;
}

const str = (value: unknown): string | null =>
  value === null || value === undefined || value === "" ? null : String(value);

/** Reads `listings` (2.1.2 backend). Falls back to the older `asins` groups,
 *  which are Amazon-only, so the form also works against the old backend. */
export function normalizeProductListings(data: any): ProductListingSummary[] {
  if (Array.isArray(data?.listings)) {
    return data.listings.map((l: any, index: number) => ({
      key: `listing-${l.id ?? index}`,
      platform: str(l.platform) ?? "amazon",
      asin: str(l.asin),
      msku: str(l.msku),
      fulfillmentChannel: str(l.fulfillment_channel),
      walmartSku: str(l.walmart_sku),
      walmartItemId: str(l.walmart_item_id),
      shopifySku: str(l.shopify_sku),
      shopifyProductId: str(l.shopify_product_id),
      listingTitle: str(l.listing_title),
      createdBy: str(l.created_by),
      isCompetitorListing: !!Number(l.is_competitor_listing ?? 0),
    }));
  }
  if (!Array.isArray(data?.asins)) return [];
  return data.asins.flatMap((group: any) => {
    const base = {
      platform: "amazon",
      asin: str(group.asin),
      walmartSku: null,
      walmartItemId: null,
      shopifySku: null,
      shopifyProductId: null,
      createdBy: str(group.created_by),
      isCompetitorListing: false,
    };
    const listings: any[] = Array.isArray(group.listings) ? group.listings : [];
    if (listings.length === 0) {
      // An ASIN linked to the product with no listing row yet.
      return [
        {
          ...base,
          key: `asin-${group.asin}`,
          msku: null,
          fulfillmentChannel: null,
          listingTitle: null,
        },
      ];
    }
    return listings.map((l: any, index: number) => ({
      ...base,
      key: `asin-${group.asin}-${l.listing_id ?? index}`,
      msku: str(l.msku),
      fulfillmentChannel: str(l.fulfillment_channel),
      listingTitle: str(l.listing_title),
    }));
  });
}

/** ASINs already linked to the product, from either response shape. Used for
 *  Decision D1: an ASIN already on this product adds another offer. */
export function existingProductAsins(data: any): Set<string> {
  const asins = new Set<string>();
  if (Array.isArray(data?.asins)) {
    for (const group of data.asins) {
      if (group?.asin) asins.add(normalizeAsin(String(group.asin)));
    }
  }
  if (Array.isArray(data?.listings)) {
    for (const l of data.listings) {
      if (l?.asin && (l.platform ?? "amazon") === "amazon") {
        asins.add(normalizeAsin(String(l.asin)));
      }
    }
  }
  return asins;
}

/** Human-readable identifiers for one listing row. */
export function describeListingIdentifiers(
  listing: ProductListingSummary
): string {
  const parts: string[] = [];
  if (listing.platform === "walmart") {
    if (listing.walmartSku) parts.push(`Walmart SKU ${listing.walmartSku}`);
    if (listing.walmartItemId) parts.push(`Item ID ${listing.walmartItemId}`);
  } else if (listing.platform === "shopify") {
    if (listing.shopifySku) parts.push(`SKU ${listing.shopifySku}`);
    if (listing.shopifyProductId) {
      parts.push(`Product ID ${listing.shopifyProductId}`);
    }
  } else {
    if (listing.asin) parts.push(`ASIN ${listing.asin}`);
    if (listing.msku) parts.push(`MSKU ${listing.msku}`);
    if (listing.fulfillmentChannel) {
      parts.push(
        listing.fulfillmentChannel === "AMAZON_NA" ? "FBA" : "Merchant"
      );
    }
  }
  return parts.length > 0 ? parts.join(" · ") : "No identifiers";
}

/** Prefer the server's message on 409 (e.g. "ASIN X belongs to product N."). */
export function listingSubmitErrorMessage(err: any): string {
  const status = err?.response?.status;
  const message = err?.response?.data?.message;
  const serverMessage = typeof message === "string" ? message : null;
  if (status === 409) {
    return (
      serverMessage ??
      "This identifier is already in use by another listing or product."
    );
  }
  if (status === 404) return serverMessage ?? "Product not found.";
  return serverMessage ?? "An error occurred. Please try again.";
}
